import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Phone, PhoneCall, PhoneOff, PhoneMissed, PhoneForwarded,
  Building2, Globe, MapPin, User, Mail, Plus, Search, 
  Filter, MoreVertical, RefreshCw, TrendingUp, Target,
  Users, CheckCircle2, XCircle, Clock, Calendar,
  ArrowRight, Sparkles, Brain, Map, BarChart3,
  Upload, Download, Flame, Thermometer, Snowflake,
  ListTodo, UserPlus, Edit, Trash2, BookOpen
} from "lucide-react";
import api, { prospectingAPI, usersAPI, prospectsAPI } from "../api";
import { PlaybookNotesSection } from "../components/PlaybookNotesSection";

const ProspectingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Cold calls data
  const [coldCalls, setColdCalls] = useState([]);
  const [coldCallsTotal, setColdCallsTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [callQueue, setCallQueue] = useState([]);
  
  // Lead scoring data
  const [scoredLeads, setScoredLeads] = useState([]);
  const [scoreStats, setScoreStats] = useState({});
  
  // Prospects list data (merged from old ProspectsPage)
  const [prospects, setProspects] = useState([]);
  const [prospectsLoading, setProspectsLoading] = useState(false);
  const [prospectsSearchTerm, setProspectsSearchTerm] = useState("");
  const [prospectsStatusFilter, setProspectsStatusFilter] = useState("all");
  const [showProspectDialog, setShowProspectDialog] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [prospectFormData, setProspectFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    job_title: '',
    status: 'new',
    source: '',
    source_type: 'manual',
    notes: '',
    score: 0,
    budget: '',
    timeline: '',
    decision_maker: false,
    pain_points: ''
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("");
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCallDialog, setShowCallDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showAIResearchDialog, setShowAIResearchDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [selectedColdCall, setSelectedColdCall] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Playbook dialog state (for prospects)
  const [showPlaybookDialog, setShowPlaybookDialog] = useState(false);
  const [selectedProspectForPlaybook, setSelectedProspectForPlaybook] = useState(null);
  
  // Task form
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    assigned_to: "",
    due_date: "",
    priority: "medium"
  });
  const [coldCallTasks, setColdCallTasks] = useState([]);
  
  // AI Research
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Geographic analysis
  const [geoData, setGeoData] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    company_name: "",
    website: "",
    phone_primary: "",
    phone_secondary: "",
    contact_name: "",
    contact_title: "",
    contact_email: "",
    city: "",
    province_state: "",
    country: "",
    industry: "",
    company_size: "",
    notes: "",
    tags: []
  });
  
  const [callOutcome, setCallOutcome] = useState({
    outcome: "no_answer",
    duration_seconds: 0,
    notes: "",
    callback_date: ""
  });

  // Fetch users for task assignment
  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  // Fetch tasks for a cold call
  const fetchColdCallTasks = async (coldCallId) => {
    try {
      const response = await prospectingAPI.getColdCallTasks(coldCallId);
      setColdCallTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setColdCallTasks([]);
    }
  };

  // Create task for cold call
  const handleCreateTask = async () => {
    if (!selectedColdCall || !taskForm.title) return;
    
    try {
      await prospectingAPI.createColdCallTask(selectedColdCall.id, taskForm);
      toast.success("Task created successfully");
      setShowTaskDialog(false);
      setTaskForm({ title: "", description: "", assigned_to: "", due_date: "", priority: "medium" });
      fetchColdCallTasks(selectedColdCall.id);
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  // Open task dialog
  const openTaskDialog = (coldCall) => {
    setSelectedColdCall(coldCall);
    setTaskForm({
      title: `Follow up with ${coldCall.company_name || coldCall.contact_name || 'prospect'}`,
      description: "",
      assigned_to: "",
      due_date: "",
      priority: "medium"
    });
    fetchColdCallTasks(coldCall.id);
    setShowTaskDialog(true);
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get("/prospecting/dashboard");
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, []);

  // Fetch cold calls
  const fetchColdCalls = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (locationFilter) params.append("province_state", locationFilter);
      
      const response = await api.get(`/prospecting/cold-calls?${params.toString()}`);
      setColdCalls(response.data.cold_calls || []);
      setColdCallsTotal(response.data.total || 0);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error("Error fetching cold calls:", error);
    }
  }, [searchTerm, statusFilter, locationFilter]);

  // Fetch call queue
  const fetchCallQueue = useCallback(async () => {
    try {
      const response = await api.get("/prospecting/cold-calls/queue?limit=10");
      setCallQueue(response.data.queue || []);
    } catch (error) {
      console.error("Error fetching call queue:", error);
    }
  }, []);

  // Fetch scored leads
  const fetchScoredLeads = useCallback(async () => {
    try {
      const response = await api.get("/prospecting/scoring/leaderboard?limit=20");
      setScoredLeads(response.data.leads || []);
      setScoreStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching scored leads:", error);
    }
  }, []);

  // Fetch prospects list (from leads collection)
  const fetchProspects = useCallback(async () => {
    setProspectsLoading(true);
    try {
      const response = await prospectsAPI.getAll();
      setProspects(response.data?.leads || response.data || []);
    } catch (error) {
      console.error("Error fetching prospects:", error);
      toast.error("Failed to fetch prospects");
    } finally {
      setProspectsLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchColdCalls(),
        fetchCallQueue(),
        fetchScoredLeads(),
        fetchUsers(),
        fetchProspects()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchColdCalls, fetchCallQueue, fetchScoredLeads, fetchUsers, fetchProspects]);

  // Refetch cold calls when filters change
  useEffect(() => {
    fetchColdCalls();
  }, [fetchColdCalls, searchTerm, statusFilter, locationFilter]);

  // Create cold call
  const handleCreateColdCall = async () => {
    try {
      await api.post("/prospecting/cold-calls", formData);
      toast.success("Cold call prospect added");
      setShowAddDialog(false);
      setFormData({
        company_name: "", website: "", phone_primary: "", phone_secondary: "",
        contact_name: "", contact_title: "", contact_email: "",
        city: "", province_state: "", country: "",
        industry: "", company_size: "", notes: "", tags: []
      });
      fetchColdCalls();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to create cold call");
    }
  };

  // Log call
  const handleLogCall = async () => {
    if (!selectedColdCall) return;
    try {
      await api.post(`/prospecting/cold-calls/${selectedColdCall.id}/log-call`, callOutcome);
      toast.success("Call logged successfully");
      setShowCallDialog(false);
      setCallOutcome({ outcome: "no_answer", duration_seconds: 0, notes: "", callback_date: "" });
      fetchColdCalls();
      fetchCallQueue();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to log call");
    }
  };

  // Convert to prospect
  const handleConvert = async () => {
    if (!selectedColdCall) return;
    try {
      const response = await api.post(`/prospecting/cold-calls/${selectedColdCall.id}/convert`, {
        status: "unqualified",
        notes: "Converted from cold calling list"
      });
      toast.success("Successfully converted to prospect!");
      setShowConvertDialog(false);
      fetchColdCalls();
      fetchDashboard();
      // Navigate to prospects page
      navigate("/sales/prospects");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert");
    }
  };

  // AI Find Lookalikes
  const handleFindLookalikes = async () => {
    setAiLoading(true);
    try {
      const response = await api.post("/prospecting/ai/find-lookalikes", {
        base_on: "top_customers",
        limit: 15
      });
      setAiSuggestions(response.data.suggestions || []);
      setAiAnalysis(response.data.analysis || null);
      toast.success(`Found ${response.data.suggestions?.length || 0} potential prospects`);
    } catch (error) {
      toast.error("AI research failed");
    } finally {
      setAiLoading(false);
    }
  };

  // Add AI suggestion to cold calls
  const handleAddSuggestion = async (suggestion) => {
    try {
      await api.post("/prospecting/cold-calls", {
        company_name: suggestion.company_name,
        industry: suggestion.industry,
        company_size: suggestion.company_size,
        city: suggestion.city,
        province_state: suggestion.province_state,
        country: suggestion.country,
        source: "ai_suggested",
        ai_match_score: suggestion.match_score,
        ai_match_reason: suggestion.match_reason,
        notes: `AI Match Score: ${suggestion.match_score}%\nReason: ${suggestion.match_reason}`
      });
      toast.success(`Added ${suggestion.company_name} to cold call list`);
      fetchColdCalls();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to add prospect");
    }
  };

  // Fetch geographic analysis
  const handleGeoAnalysis = async (groupBy = "province_state") => {
    try {
      const response = await api.post("/prospecting/ai/geographic-analysis", {
        group_by: groupBy,
        include_prospects: true,
        include_cold_calls: true,
        include_contacts: true
      });
      setGeoData(response.data);
    } catch (error) {
      toast.error("Failed to load geographic data");
    }
  };

  // ============ PROSPECTS LIST HANDLERS (merged from ProspectsPage) ============
  
  const prospectStatusColors = {
    new: 'bg-blue-100 text-blue-800',
    unqualified: 'bg-slate-100 text-slate-800',
    contacted: 'bg-purple-100 text-purple-800',
    qualified: 'bg-green-100 text-green-800',
    nurturing: 'bg-amber-100 text-amber-800',
    converted: 'bg-emerald-100 text-emerald-800',
    lost: 'bg-red-100 text-red-800'
  };

  const handleProspectFormChange = (field, value) => {
    setProspectFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProspectSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...prospectFormData,
      score: parseInt(prospectFormData.score) || 0,
      budget: prospectFormData.budget ? parseFloat(prospectFormData.budget) : null
    };
    
    try {
      if (editingProspect) {
        await prospectsAPI.update(editingProspect.id, submitData);
        toast.success('Prospect updated');
      } else {
        await prospectsAPI.create(submitData);
        toast.success('Prospect created');
      }
      setShowProspectDialog(false);
      resetProspectForm();
      fetchProspects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save prospect');
    }
  };

  const handleEditProspect = (prospect) => {
    setEditingProspect(prospect);
    setProspectFormData({
      first_name: prospect.first_name || '',
      last_name: prospect.last_name || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      company_name: prospect.company_name || '',
      job_title: prospect.job_title || '',
      status: prospect.status || 'new',
      source: prospect.source || '',
      source_type: prospect.source_type || 'manual',
      notes: prospect.notes || '',
      score: prospect.score || 0,
      budget: prospect.budget || '',
      timeline: prospect.timeline || '',
      decision_maker: prospect.decision_maker || false,
      pain_points: prospect.pain_points || ''
    });
    setShowProspectDialog(true);
  };

  const handleDeleteProspect = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prospect?')) return;
    try {
      await prospectsAPI.delete(id);
      toast.success('Prospect deleted');
      fetchProspects();
    } catch (error) {
      toast.error('Failed to delete prospect');
    }
  };

  const handleConvertProspect = async (id) => {
    if (!window.confirm('Convert this prospect to a contact?')) return;
    try {
      await prospectsAPI.convert(id);
      toast.success('Prospect converted to contact');
      fetchProspects();
    } catch (error) {
      toast.error('Failed to convert prospect');
    }
  };

  const resetProspectForm = () => {
    setEditingProspect(null);
    setProspectFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      job_title: '',
      status: 'new',
      source: '',
      source_type: 'manual',
      notes: '',
      score: 0,
      budget: '',
      timeline: '',
      decision_maker: false,
      pain_points: ''
    });
  };

  const filteredProspects = prospects.filter(prospect => {
    const matchesSearch = 
      (prospect.first_name || '').toLowerCase().includes(prospectsSearchTerm.toLowerCase()) ||
      (prospect.last_name || '').toLowerCase().includes(prospectsSearchTerm.toLowerCase()) ||
      (prospect.email || '').toLowerCase().includes(prospectsSearchTerm.toLowerCase()) ||
      (prospect.company_name || '').toLowerCase().includes(prospectsSearchTerm.toLowerCase());
    const matchesStatus = prospectsStatusFilter === 'all' || prospect.status === prospectsStatusFilter;
    return matchesSearch && matchesStatus;
  });

  // ============ END PROSPECTS LIST HANDLERS ============

  const getStatusBadgeVariant = (status) => {
    const variants = {
      new: "outline",
      queued: "secondary",
      contacted: "default",
      interested: "default",
      qualified: "default",
      converted: "default",
      callback: "secondary",
      not_interested: "destructive",
      do_not_call: "destructive"
    };
    return variants[status] || "outline";
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "text-slate-600",
      queued: "text-blue-600",
      contacted: "text-purple-600",
      interested: "text-green-600",
      qualified: "text-emerald-600",
      converted: "text-green-700 bg-green-50",
      callback: "text-amber-600",
      not_interested: "text-red-500",
      do_not_call: "text-red-700"
    };
    return colors[status] || "";
  };

  const getScoreBadge = (score, level) => {
    if (level === "hot" || score >= 80) {
      return <Badge className="bg-red-500 text-white"><Flame className="h-3 w-3 mr-1" />{score}</Badge>;
    } else if (level === "warm" || score >= 50) {
      return <Badge className="bg-orange-500 text-white"><Thermometer className="h-3 w-3 mr-1" />{score}</Badge>;
    } else if (score > 0) {
      return <Badge className="bg-blue-500 text-white"><Snowflake className="h-3 w-3 mr-1" />{score}</Badge>;
    }
    return <Badge variant="outline">New</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prospecting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Prospecting & Cold Calling</h1>
          <p className="text-muted-foreground">Manage cold calls, lead scoring, and AI-powered prospect research</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowAIResearchDialog(true)}>
            <Brain className="h-4 w-4 mr-2" />
            AI Research
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-prospect-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Prospect
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="prospects">Prospects List</TabsTrigger>
          <TabsTrigger value="cold-calls">Cold Calls</TabsTrigger>
          <TabsTrigger value="call-queue">Call Queue</TabsTrigger>
          <TabsTrigger value="lead-scoring">Lead Scoring</TabsTrigger>
          <TabsTrigger value="geography">Geographic Analysis</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Prospects</p>
                    <p className="text-2xl font-bold">{dashboardData?.cold_calls?.total || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">In Queue</p>
                    <p className="text-2xl font-bold">{dashboardData?.cold_calls?.in_queue || 0}</p>
                  </div>
                  <Phone className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Interested</p>
                    <p className="text-2xl font-bold text-green-600">{dashboardData?.cold_calls?.interested || 0}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Converted</p>
                    <p className="text-2xl font-bold text-emerald-600">{dashboardData?.cold_calls?.converted || 0}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Callbacks</p>
                    <p className="text-2xl font-bold text-amber-600">{dashboardData?.cold_calls?.callbacks_pending || 0}</p>
                  </div>
                  <PhoneForwarded className="h-8 w-8 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversion</p>
                    <p className="text-2xl font-bold">{dashboardData?.cold_calls?.conversion_rate || 0}%</p>
                  </div>
                  <Target className="h-8 w-8 text-indigo-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Lead Scoring Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-red-500" />
                  Lead Temperature
                </CardTitle>
                <CardDescription>Prospects scored by engagement</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <Flame className="h-6 w-6 text-red-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-red-600">{dashboardData?.lead_scoring?.hot || 0}</p>
                    <p className="text-xs text-muted-foreground">Hot (80+)</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <Thermometer className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-orange-600">{dashboardData?.lead_scoring?.warm || 0}</p>
                    <p className="text-xs text-muted-foreground">Warm (50-79)</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <Snowflake className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-blue-600">{dashboardData?.lead_scoring?.cold || 0}</p>
                    <p className="text-xs text-muted-foreground">Cold (1-49)</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <Users className="h-6 w-6 text-slate-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-slate-600">{dashboardData?.lead_scoring?.unscored || 0}</p>
                    <p className="text-xs text-muted-foreground">Unscored</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneCall className="h-5 w-5 text-green-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.recent_activity?.recent_calls?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboardData.recent_activity.recent_calls.map((call) => (
                      <div key={call.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{call.company_name}</span>
                        </div>
                        <Badge variant={getStatusBadgeVariant(call.status)} className={getStatusColor(call.status)}>
                          {call.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No recent calls</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setActiveTab("call-queue")}>
              <CardContent className="pt-6 text-center">
                <PhoneCall className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <h3 className="font-semibold">Start Calling</h3>
                <p className="text-sm text-muted-foreground">Work through your call queue</p>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setShowAIResearchDialog(true)}>
              <CardContent className="pt-6 text-center">
                <Brain className="h-10 w-10 text-purple-500 mx-auto mb-2" />
                <h3 className="font-semibold">AI Research</h3>
                <p className="text-sm text-muted-foreground">Find lookalike prospects</p>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:bg-slate-50" onClick={() => { setActiveTab("geography"); handleGeoAnalysis(); }}>
              <CardContent className="pt-6 text-center">
                <Map className="h-10 w-10 text-blue-500 mx-auto mb-2" />
                <h3 className="font-semibold">Geographic View</h3>
                <p className="text-sm text-muted-foreground">Analyze by location</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Prospects List Tab (merged from old ProspectsPage) */}
        <TabsContent value="prospects" className="space-y-4">
          {/* Header & Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search prospects..."
                  className="pl-10"
                  value={prospectsSearchTerm}
                  onChange={(e) => setProspectsSearchTerm(e.target.value)}
                  data-testid="prospects-search-input"
                />
              </div>
              <Select value={prospectsStatusFilter} onValueChange={setProspectsStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="prospects-filter-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="unqualified">Unqualified</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={() => {
                resetProspectForm();
                setShowProspectDialog(true);
              }}
              data-testid="add-new-prospect-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Prospect
            </Button>
          </div>

          {/* Prospects Table */}
          <Card>
            <CardContent className="p-0">
              {prospectsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProspects.length > 0 ? (
                      filteredProspects.map((prospect) => (
                        <TableRow key={prospect.id} data-testid={`prospect-row-${prospect.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
                                <Target className="h-4 w-4 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium">{prospect.first_name} {prospect.last_name}</p>
                                {prospect.job_title && (
                                  <p className="text-xs text-muted-foreground">{prospect.job_title}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                {prospect.email}
                              </div>
                              {prospect.phone && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Phone className="h-3 w-3 text-muted-foreground" />
                                  {prospect.phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {prospect.company_name ? (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                {prospect.company_name}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              prospect.score >= 70 ? 'border-emerald-500 text-emerald-700' :
                              prospect.score >= 40 ? 'border-amber-500 text-amber-700' :
                              'border-slate-300'
                            }>
                              {prospect.score || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={prospectStatusColors[prospect.status] || 'bg-slate-100'}>
                              {prospect.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {prospect.status !== 'converted' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleConvertProspect(prospect.id)}
                                  title="Convert to Contact"
                                  data-testid={`convert-prospect-${prospect.id}`}
                                >
                                  <ArrowRight className="h-4 w-4 text-emerald-600" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedProspectForPlaybook(prospect);
                                  setShowPlaybookDialog(true);
                                }}
                                title="Playbook Notes"
                                data-testid={`playbook-prospect-${prospect.id}`}
                              >
                                <BookOpen className="h-4 w-4 text-purple-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditProspect(prospect)}
                                data-testid={`edit-prospect-${prospect.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteProspect(prospect.id)}
                                data-testid={`delete-prospect-${prospect.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No prospects found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cold Calls Tab */}
        <TabsContent value="cold-calls" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies, contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="callback">Callback</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="not_interested">Not Interested</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              placeholder="Province/State"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-[150px]"
            />
            
            <Button variant="outline" onClick={fetchColdCalls}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Status Summary */}
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(statusCounts).filter(([_, count]) => count > 0).map(([status, count]) => (
              <Badge 
                key={status} 
                variant={statusFilter === status ? "default" : "outline"}
                className={`cursor-pointer ${getStatusColor(status)}`}
                onClick={() => setStatusFilter(statusFilter === status ? "all" : status)}
              >
                {status.replace("_", " ")}: {count}
              </Badge>
            ))}
          </div>

          {/* Cold Calls Table */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-medium">Company</th>
                    <th className="text-left p-3 font-medium">Contact</th>
                    <th className="text-left p-3 font-medium">Phone</th>
                    <th className="text-left p-3 font-medium">Location</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Calls</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coldCalls.map((call) => (
                    <tr key={call.id} className="border-t hover:bg-slate-50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{call.company_name}</p>
                          {call.website && (
                            <a href={call.website.startsWith("http") ? call.website : `https://${call.website}`} 
                               target="_blank" rel="noopener noreferrer"
                               className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              {call.website}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {call.contact_name && (
                          <div>
                            <p className="text-sm">{call.contact_name}</p>
                            {call.contact_title && <p className="text-xs text-muted-foreground">{call.contact_title}</p>}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        {call.phone_primary && (
                          <a href={`tel:${call.phone_primary}`} className="text-sm hover:text-blue-500">
                            {call.phone_primary}
                          </a>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="text-sm">
                          {[call.city, call.province_state, call.country].filter(Boolean).join(", ")}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant={getStatusBadgeVariant(call.status)} className={getStatusColor(call.status)}>
                          {call.status?.replace("_", " ")}
                        </Badge>
                        {call.source === "ai_suggested" && (
                          <Badge variant="outline" className="ml-1 text-purple-600">
                            <Sparkles className="h-3 w-3 mr-1" />AI
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm">{call.call_count || 0}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedColdCall(call); setShowCallDialog(true); }}
                            disabled={call.status === "converted" || call.status === "do_not_call"}
                            title="Log Call"
                          >
                            <PhoneCall className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openTaskDialog(call)}
                            title="Assign Task"
                          >
                            <ListTodo className="h-4 w-4" />
                          </Button>
                          {(call.status === "interested" || call.status === "qualified") && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-green-600"
                              onClick={() => { setSelectedColdCall(call); setShowConvertDialog(true); }}
                              title="Convert to Prospect"
                            >
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {coldCalls.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No cold call prospects found</p>
                  <Button variant="outline" className="mt-2" onClick={() => setShowAddDialog(true)}>
                    Add Your First Prospect
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Call Queue Tab */}
        <TabsContent value="call-queue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PhoneCall className="h-5 w-5 text-green-500" />
                Next to Call
              </CardTitle>
              <CardDescription>Prioritized list of prospects to call</CardDescription>
            </CardHeader>
            <CardContent>
              {callQueue.length > 0 ? (
                <div className="space-y-3">
                  {callQueue.map((call, index) => (
                    <div 
                      key={call.id} 
                      className={`p-4 border rounded-lg ${index === 0 ? 'border-green-500 bg-green-50' : 'hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === 0 ? 'bg-green-500 text-white' : 'bg-slate-200'}`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold">{call.company_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {call.contact_name && <span>{call.contact_name}</span>}
                              {call.phone_primary && (
                                <a href={`tel:${call.phone_primary}`} className="text-blue-500 hover:underline">
                                  {call.phone_primary}
                                </a>
                              )}
                            </div>
                            {call.city && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {[call.city, call.province_state].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusBadgeVariant(call.status)} className={getStatusColor(call.status)}>
                            {call.status?.replace("_", " ")}
                          </Badge>
                          <Button 
                            variant={index === 0 ? "default" : "outline"}
                            onClick={() => { setSelectedColdCall(call); setShowCallDialog(true); }}
                          >
                            <Phone className="h-4 w-4 mr-2" />
                            {index === 0 ? "Call Now" : "Call"}
                          </Button>
                        </div>
                      </div>
                      {call.next_callback && (
                        <div className="mt-2 flex items-center gap-1 text-sm text-amber-600">
                          <Calendar className="h-3 w-3" />
                          Callback: {new Date(call.next_callback).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p className="font-medium">Queue is empty!</p>
                  <p className="text-sm">Add more prospects to continue calling</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead Scoring Tab */}
        <TabsContent value="lead-scoring" className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-red-50">
              <CardContent className="pt-4 text-center">
                <Flame className="h-8 w-8 text-red-500 mx-auto" />
                <p className="text-2xl font-bold text-red-600">{scoreStats.hot || 0}</p>
                <p className="text-sm">Hot Leads (80+)</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50">
              <CardContent className="pt-4 text-center">
                <Thermometer className="h-8 w-8 text-orange-500 mx-auto" />
                <p className="text-2xl font-bold text-orange-600">{scoreStats.warm || 0}</p>
                <p className="text-sm">Warm Leads (50-79)</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="pt-4 text-center">
                <Snowflake className="h-8 w-8 text-blue-500 mx-auto" />
                <p className="text-2xl font-bold text-blue-600">{scoreStats.cold || 0}</p>
                <p className="text-sm">Cold Leads (1-49)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <Users className="h-8 w-8 text-slate-500 mx-auto" />
                <p className="text-2xl font-bold">{scoreStats.total_scored || 0}</p>
                <p className="text-sm">Total Scored</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Lead Leaderboard</CardTitle>
              <CardDescription>Prospects ranked by engagement score</CardDescription>
            </CardHeader>
            <CardContent>
              {scoredLeads.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3">Rank</th>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Company</th>
                      <th className="text-left p-3">Score</th>
                      <th className="text-left p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoredLeads.map((lead, index) => (
                      <tr key={lead.id} className="border-t hover:bg-slate-50">
                        <td className="p-3">
                          <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-sm ${index < 3 ? 'bg-yellow-500 text-white' : 'bg-slate-200'}`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="p-3">
                          <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                          <p className="text-xs text-muted-foreground">{lead.email}</p>
                        </td>
                        <td className="p-3">{lead.company_name}</td>
                        <td className="p-3">
                          {getScoreBadge(lead.engagement_score, lead.score_level)}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{lead.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No scored leads yet</p>
                  <p className="text-sm">Lead scores are calculated based on engagement activities</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Geographic Analysis Tab */}
        <TabsContent value="geography" className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Button variant={geoData?.group_by === "city" ? "default" : "outline"} onClick={() => handleGeoAnalysis("city")}>
              By City
            </Button>
            <Button variant={geoData?.group_by === "province_state" ? "default" : "outline"} onClick={() => handleGeoAnalysis("province_state")}>
              By Province/State
            </Button>
            <Button variant={geoData?.group_by === "country" ? "default" : "outline"} onClick={() => handleGeoAnalysis("country")}>
              By Country
            </Button>
          </div>

          {geoData ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Contacts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Customers by {geoData.group_by?.replace("_", " ")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {geoData.results?.contacts?.length > 0 ? (
                    <div className="space-y-2">
                      {geoData.results.contacts.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm">{item.location || "Unknown"}</span>
                          <Badge variant="secondary">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No data</p>
                  )}
                </CardContent>
              </Card>

              {/* Prospects */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Prospects by {geoData.group_by?.replace("_", " ")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {geoData.results?.prospects?.length > 0 ? (
                    <div className="space-y-2">
                      {geoData.results.prospects.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm">{item.location || "Unknown"}</span>
                          <Badge variant="secondary">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No data</p>
                  )}
                </CardContent>
              </Card>

              {/* Cold Calls */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Cold Calls by {geoData.group_by?.replace("_", " ")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {geoData.results?.cold_calls?.length > 0 ? (
                    <div className="space-y-2">
                      {geoData.results.cold_calls.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm">{item.location || "Unknown"}</span>
                          <div className="flex items-center gap-1">
                            <Badge variant="secondary">{item.count}</Badge>
                            {item.converted > 0 && (
                              <Badge className="bg-green-500 text-white text-xs">{item.converted} conv</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-8">
              <Button onClick={() => handleGeoAnalysis()}>
                <Map className="h-4 w-4 mr-2" />
                Load Geographic Data
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Cold Call Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Cold Call Prospect</DialogTitle>
            <DialogDescription>Add a new company to your cold calling list</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input 
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder="Company name"
              />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input 
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="www.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Primary Phone</Label>
              <Input 
                value={formData.phone_primary}
                onChange={(e) => setFormData({...formData, phone_primary: e.target.value})}
                placeholder="+1 234 567 8900"
              />
            </div>
            <div className="space-y-2">
              <Label>Secondary Phone</Label>
              <Input 
                value={formData.phone_secondary}
                onChange={(e) => setFormData({...formData, phone_secondary: e.target.value})}
                placeholder="+1 234 567 8901"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Name</Label>
              <Input 
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Title</Label>
              <Input 
                value={formData.contact_title}
                onChange={(e) => setFormData({...formData, contact_title: e.target.value})}
                placeholder="CEO, Manager, etc."
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input 
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Industry</Label>
              <Input 
                value={formData.industry}
                onChange={(e) => setFormData({...formData, industry: e.target.value})}
                placeholder="Manufacturing, Retail, etc."
              />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input 
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label>Province/State</Label>
              <Input 
                value={formData.province_state}
                onChange={(e) => setFormData({...formData, province_state: e.target.value})}
                placeholder="Province or State"
              />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input 
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                placeholder="Country"
              />
            </div>
            <div className="space-y-2">
              <Label>Company Size</Label>
              <Select value={formData.company_size} onValueChange={(v) => setFormData({...formData, company_size: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (1-50)</SelectItem>
                  <SelectItem value="medium">Medium (51-200)</SelectItem>
                  <SelectItem value="large">Large (201-1000)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (1000+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any notes about this prospect..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateColdCall} disabled={!formData.company_name}>
              Add Prospect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Call Dialog */}
      <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call: {selectedColdCall?.company_name}</DialogTitle>
            <DialogDescription>
              {selectedColdCall?.phone_primary && (
                <a href={`tel:${selectedColdCall.phone_primary}`} className="text-blue-500 hover:underline flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {selectedColdCall.phone_primary}
                </a>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Call Outcome *</Label>
              <Select value={callOutcome.outcome} onValueChange={(v) => setCallOutcome({...callOutcome, outcome: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Connected - Spoke to Contact</SelectItem>
                  <SelectItem value="interested">Interested</SelectItem>
                  <SelectItem value="callback_scheduled">Callback Scheduled</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="not_interested">Not Interested</SelectItem>
                  <SelectItem value="wrong_number">Wrong Number</SelectItem>
                  <SelectItem value="do_not_call">Do Not Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {callOutcome.outcome === "callback_scheduled" && (
              <div className="space-y-2">
                <Label>Callback Date/Time</Label>
                <Input 
                  type="datetime-local"
                  value={callOutcome.callback_date}
                  onChange={(e) => setCallOutcome({...callOutcome, callback_date: e.target.value})}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Duration (seconds)</Label>
              <Input 
                type="number"
                value={callOutcome.duration_seconds}
                onChange={(e) => setCallOutcome({...callOutcome, duration_seconds: parseInt(e.target.value) || 0})}
                placeholder="0"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={callOutcome.notes}
                onChange={(e) => setCallOutcome({...callOutcome, notes: e.target.value})}
                placeholder="Call notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallDialog(false)}>Cancel</Button>
            <Button onClick={handleLogCall}>
              <PhoneCall className="h-4 w-4 mr-2" />
              Log Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Prospect Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Prospect</DialogTitle>
            <DialogDescription>
              Convert {selectedColdCall?.company_name} from cold call to active prospect
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Ready to Convert</span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              This will create a new prospect in your Sales pipeline and mark this cold call as converted.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button onClick={handleConvert} className="bg-green-600 hover:bg-green-700">
              <ArrowRight className="h-4 w-4 mr-2" />
              Convert to Prospect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Research Dialog */}
      <Dialog open={showAIResearchDialog} onOpenChange={setShowAIResearchDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              AI Prospect Research
            </DialogTitle>
            <DialogDescription>
              Find lookalike prospects based on your best customers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button onClick={handleFindLookalikes} disabled={aiLoading}>
                {aiLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Find Lookalike Prospects
              </Button>
            </div>
            
            {aiAnalysis && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Customer Pattern Analysis</CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    {aiAnalysis.top_industries && Object.keys(aiAnalysis.top_industries).length > 0 && (
                      <div>
                        <p className="font-medium mb-1">Top Industries:</p>
                        {Object.entries(aiAnalysis.top_industries).map(([ind, count]) => (
                          <Badge key={ind} variant="outline" className="mr-1 mb-1">{ind} ({count})</Badge>
                        ))}
                      </div>
                    )}
                    {aiAnalysis.top_provinces && Object.keys(aiAnalysis.top_provinces).length > 0 && (
                      <div>
                        <p className="font-medium mb-1">Top Regions:</p>
                        {Object.entries(aiAnalysis.top_provinces).map(([prov, count]) => (
                          <Badge key={prov} variant="secondary" className="mr-1 mb-1">{prov} ({count})</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {aiSuggestions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">AI Suggested Prospects ({aiSuggestions.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {aiSuggestions.map((suggestion, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{suggestion.company_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {suggestion.industry && <Badge variant="outline">{suggestion.industry}</Badge>}
                              {suggestion.city && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {[suggestion.city, suggestion.province_state, suggestion.country].filter(Boolean).join(", ")}
                                </span>
                              )}
                            </div>
                            {suggestion.match_reason && (
                              <p className="text-xs text-muted-foreground mt-1">{suggestion.match_reason}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {suggestion.match_score && (
                              <Badge className="bg-purple-500 text-white">{suggestion.match_score}% match</Badge>
                            )}
                            <Button size="sm" onClick={() => handleAddSuggestion(suggestion)}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Assignment Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Follow-up Task</DialogTitle>
            <DialogDescription>
              Create a task for {selectedColdCall?.company_name || selectedColdCall?.contact_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Task Title</Label>
              <Input 
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                placeholder="e.g., Follow up call with prospect"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                placeholder="Add details about this task..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <Select 
                  value={taskForm.assigned_to} 
                  onValueChange={(v) => setTaskForm({...taskForm, assigned_to: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm({...taskForm, priority: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Due Date</Label>
              <Input 
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
              />
            </div>

            {coldCallTasks.length > 0 && (
              <div>
                <Label className="mb-2 block">Existing Tasks</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {coldCallTasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <span>{task.title}</span>
                      <Badge variant={task.status === 'pending' ? 'secondary' : 'outline'}>
                        {task.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={!taskForm.title}>
              <ListTodo className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Prospect Dialog */}
      <Dialog open={showProspectDialog} onOpenChange={(open) => {
        setShowProspectDialog(open);
        if (!open) resetProspectForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProspect ? 'Edit Prospect' : 'Add New Prospect'}</DialogTitle>
            <DialogDescription>
              {editingProspect ? 'Update prospect information' : 'Create a new prospect record'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProspectSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prospect_first_name">First Name *</Label>
                <Input
                  id="prospect_first_name"
                  value={prospectFormData.first_name}
                  onChange={(e) => handleProspectFormChange('first_name', e.target.value)}
                  required
                  data-testid="prospect-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prospect_last_name">Last Name *</Label>
                <Input
                  id="prospect_last_name"
                  value={prospectFormData.last_name}
                  onChange={(e) => handleProspectFormChange('last_name', e.target.value)}
                  required
                  data-testid="prospect-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="prospect_email">Email *</Label>
              <Input
                id="prospect_email"
                type="email"
                value={prospectFormData.email}
                onChange={(e) => handleProspectFormChange('email', e.target.value)}
                required
                data-testid="prospect-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prospect_phone">Phone</Label>
              <Input
                id="prospect_phone"
                value={prospectFormData.phone}
                onChange={(e) => handleProspectFormChange('phone', e.target.value)}
                data-testid="prospect-phone-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prospect_company_name">Company</Label>
                <Input
                  id="prospect_company_name"
                  value={prospectFormData.company_name}
                  onChange={(e) => handleProspectFormChange('company_name', e.target.value)}
                  data-testid="prospect-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prospect_job_title">Job Title</Label>
                <Input
                  id="prospect_job_title"
                  value={prospectFormData.job_title}
                  onChange={(e) => handleProspectFormChange('job_title', e.target.value)}
                  data-testid="prospect-jobtitle-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prospect_status">Status</Label>
                <Select value={prospectFormData.status} onValueChange={(v) => handleProspectFormChange('status', v)}>
                  <SelectTrigger data-testid="prospect-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="unqualified">Unqualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prospect_score">Lead Score</Label>
                <Input
                  id="prospect_score"
                  type="number"
                  min="0"
                  max="100"
                  value={prospectFormData.score}
                  onChange={(e) => handleProspectFormChange('score', e.target.value)}
                  data-testid="prospect-score-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="prospect_source">Source</Label>
              <Input
                id="prospect_source"
                value={prospectFormData.source}
                onChange={(e) => handleProspectFormChange('source', e.target.value)}
                placeholder="e.g., Website, Referral, Cold Call"
                data-testid="prospect-source-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prospect_notes">Notes</Label>
              <Textarea
                id="prospect_notes"
                value={prospectFormData.notes}
                onChange={(e) => handleProspectFormChange('notes', e.target.value)}
                rows={3}
                data-testid="prospect-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="prospect-submit-btn">
                {editingProspect ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Playbook Notes Dialog for Prospects */}
      <Dialog open={showPlaybookDialog} onOpenChange={(open) => {
        setShowPlaybookDialog(open);
        if (!open) setSelectedProspectForPlaybook(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-600" />
              Playbook Notes - {selectedProspectForPlaybook?.first_name} {selectedProspectForPlaybook?.last_name}
            </DialogTitle>
            <DialogDescription>
              Track sales playbook progress and notes for this prospect
            </DialogDescription>
          </DialogHeader>
          {selectedProspectForPlaybook && (
            <div className="mt-4">
              <PlaybookNotesSection 
                entityType="prospect" 
                entityId={selectedProspectForPlaybook.id}
                className="w-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProspectingPage;
